import React, { useState, useEffect } from 'react';
import { 
  CircularProgress,
  Grid,
  Box,
  Typography,
  TextField as MuiTextField,
  FormControl,
  Select as MuiSelect
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { pick } from 'lodash';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MenuItem from '@mui/material/MenuItem';
import useUser from '../lib/useUser';
import { apiFetch } from '../lib/fetch';
import Layout from '../components/layout';
import IncomePlanWrapper from '../components/income-plan-wrapper';
import { Button, Select } from '../lib/common';
import { formatCurrency } from '../lib/util';
import CustomSelect from '../components/custom-select';
import { colors } from '../lib/styles';
import { toggleForm, generateIncomeByYear } from '../lib/client';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
);

const styles = {
  mainBody: {
    padding: '20px',
  },
  errTxt: {
    color: 'red',
  },
}

const ClientIncomeYear = () => {
  const auth = useUser();
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [err, setErr] = useState(null);
  const [planIncomeAfterTax, setPlanIncomeAfterTax] = useState('true');
  const [selectedYear, setSelectedYear] = useState(null);
  const [viewAdditionalInformation, setViewAdditionalInformation] = useState(false);
  const [savingView, setSavingView] = useState(false);

  useEffect(() => {
    if (!auth?.id) return;
    apiFetch(`/users/${auth.id}/ip_households/${id}`)
    .then((res) => {
      return generateIncomeByYear(res);
    }).then((generatedClient) => {
      setClient(generatedClient);
    }).catch((e) => {
      let err = e.toString();
      setErr(err);
    });
  }, [auth?.id, id]);

  useEffect(() => {
    if (client && client.id) {
      const recalculateProjection = async () => {
        try {
          const updatedClient = await generateIncomeByYear(client);
          setClient(updatedClient);
        } catch (e) {
          console.error('Error recalculating projection:', e);
        }
      };
      recalculateProjection();
    }
  }, [
    client?.inflation_rate,
    client?.effective_tax_rate,
    client?.filing_status,
    client?.leg1_tax_free,
    client?.leg2_tax_free,
    client?.leg3_tax_free,
  ]);

  if (!client) {
    return <Layout><div>Loading...</div></Layout>;
  }

  console.log('ClientIncomeYear client:', client);

  const displayName = client.displayName;
  

  const handleTextChange = (evt) => {
    const { name, value } = evt.target;
    const parsedValue = value === 'true' ? true : value === 'false' ? false : value;
    setClient({ ...client, [name]: parsedValue });
  };

  const handleSubmit = async () => {
    setErr(null);
    const toSave = pick(client, [
      'inflation_rate',
      'filing_status',
      'leg1_tax_free',
      'leg2_tax_free',
      'leg3_tax_free',
      'effective_tax_rate',
    ]);
    try {
      const saved = await apiFetch.put(`/users/${auth.id}/ip_households/${id}`, toSave);
      saved.assets = client.assets;
      const updatedClient = await generateIncomeByYear(saved);
      setClient(updatedClient);
      return updatedClient;
    } catch (e) {
      let err = e.toString();
      setErr(err);
    }
    return null;
  };

  const data = client.incomeByYearData || [];
  const rows = data.map((row, index) => {
    const { year, yearAge, yearSpouseAge } = row;
    const tableCellStyle = {
      padding: '8px 10px',
      textAlign: 'right',
      borderBottom: '1px solid #f0f0f0',
      color: '#333',
      whiteSpace: 'nowrap'
    };
    
    const tableCellCenterStyle = {
      ...tableCellStyle,
      textAlign: 'center',
    };
    
    const negativeValueStyle = {
      ...tableCellStyle,
      color: '#e53935'
    };
    
    let w2Comp;
    let w22Comp;
    

    w2Comp = <td style={tableCellStyle}>${formatCurrency(row.yearW2Income || "0.00")}</td>;
    w22Comp = <td style={tableCellStyle}>${formatCurrency(row.yearW22Income || "0.00")}</td>;
    // Determine whether a value is negative and should use the negative style
    const getValueStyle = (value) => {
      return value < 0 ? negativeValueStyle : tableCellStyle;
    };
    
    const rowCells = [
      <td
        style={{
          ...tableCellCenterStyle,
          position: 'sticky',
          left: -1,
          zIndex: 1,
          backgroundColor: index % 2 === 0 ? '#fff' : '#e9ebff',
          boxShadow: '2px 0 5px -2px rgba(0,0,0,0.2)',
          fontWeight: 'bold',
        }}
        onClick={() => {
          setSelectedYear(year);
        }}
      >
        {year}
      </td>,
      <td style={tableCellCenterStyle}>{yearAge}</td>,
      <td style={tableCellCenterStyle}>{yearSpouseAge}</td>,
      w2Comp,
      w22Comp,
      <td style={tableCellStyle}>${formatCurrency((row.yearPensionIncome + row.yearSpousePensionIncome) || "0.00")}</td>,
      <td style={tableCellStyle}>${formatCurrency((row.yearRentalIncome + row.yearSpouseRentalIncome) || "0.00")}</td>,
      <td style={{
        ...tableCellStyle,
        borderLeft: '2px solid black',
      }}>${formatCurrency(row.yearSSI || "0.00")}</td>,
      <td style={{
        ...tableCellStyle,
        borderRight: '2px solid black',
      }}>${formatCurrency(row.yearSSI2 || "0.00")}</td>,
    ];
    
    if (viewAdditionalInformation) {
      rowCells.push(
        <td style={tableCellStyle}>${formatCurrency(row.yearProvisionalIncome || "0.00")}</td>,
        <td style={tableCellStyle}>{formatCurrency(row.yearSSITaxRate * 100 || "0.00")}%</td>,
        <td style={tableCellStyle}>${formatCurrency(row.yearTaxableSSI || "0.00")}</td>,
        <td style={tableCellStyle}>${formatCurrency(row.yearTaxableIncome || "0.00")}</td>,
        <td style={tableCellStyle}>{row.yearEstimatedTax > 0 ? '-' : ''}${formatCurrency(row.yearEstimatedTax || "0.00")}</td>,
      );
    }

    rowCells.push(
      <td style={{
        ...tableCellStyle,
        borderTop: '2px solid black',
        borderBottom: '2px solid black',
        borderLeft: '2px solid black',
        borderRight: '1px solid black',
        backgroundColor: 'rgba(185,208,246, 0.6)'
      }}>${formatCurrency(row.baseIncomeAfterTax || "0.00")}</td>,
      <td style={{
        ...tableCellStyle,
        borderTop: '2px solid black',
        borderBottom: '2px solid black',
        borderRight: '2px solid black',
        borderLeft: '1px solid black',
        backgroundColor: 'rgba(140,178,240, 0.6)'
      }}>${formatCurrency(row.yearDesiredIncome || "0.00")}</td>,
      <td style={{
        ...tableCellStyle,
        fontWeight: 'bold',
        backgroundColor: row.baseIncomeAfterTax >= row.yearDesiredIncome ? 'rgba(0, 200, 0, 0.2)' : 'rgba(255, 0, 0, 0.2)'
      }}>{row.yearGap > 0 ? '-' : ''}${formatCurrency(row.yearGap || "0.00")}</td>,
      <td style={tableCellStyle}>${formatCurrency(row.leg1Income || "0.00")}</td>,
      <td style={tableCellStyle}>${formatCurrency(row.leg2Income || "0.00")}</td>,
      <td style={tableCellStyle}>${formatCurrency(row.leg3Income || "0.00")}</td>,
      <td style={{
        ...tableCellStyle,
        backgroundColor: 'rgba(140,178,240, 0.6)'
      }}>{row.planIncomeTaxes < 0 ? '-' : ''}${formatCurrency(row.planIncomeTaxes || "0.00")}</td>,
      <td style={{
        ...tableCellStyle,
        color: '#000',
        backgroundColor: row.totalPlanIncomeAfterTax > row.yearDesiredIncome ? 'rgba(0, 200, 0, 0.2)' : 'rgba(255, 0, 0, 0.2)'
      }}>${formatCurrency(row.totalPlanIncomeAfterTax || "0.00")}</td>,
      <td style={{
        ...tableCellStyle,
        color: '#000',
        backgroundColor: row.planGap >= 0 ? 'rgba(0, 200, 0, 0.5)' : 'rgba(255, 0, 0, 0.5)'
      }}>{row.planGap < 0 ? '-' : ''}${formatCurrency(row.planGap || "0.00")}</td>
    );
    
    return rowCells;
  });

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        color: colors.black,
        display: true,
        text: 'Total Projected Income by Year',
        font: {
          size: 24,
          weight: 'bold',
        },
      },
      tooltip: {
        bodyFont: { size: 18 },
        titleFont: { size: 18 },
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return `$${value.toLocaleString()}`;
          }
        }
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => `$${value.toLocaleString()}`,
          color: colors.black,
        },
      },
      x: {
        ticks: {
          color: colors.black,
        },
      },
    }
  };

  const chartData = {
    labels: data.map((row) => row.year),
    datasets: [
      {
        data: data.map((row) => Number(row.totalPlanIncomeAfterTax).toFixed(2)),
        borderColor: colors.primaryColor,
        backgroundColor: 'rgba(142, 160, 196, 0.4)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  return (
    <IncomePlanWrapper handleSubmit={handleSubmit}>
      <Box style={styles.mainBody}>
        <div style={styles.errTxt}>{err?.toString()}</div>
        {client ? (
          <div style={{ margin: '10px' }}>
            <Grid container style={{ width: '100%' }}>
              <Grid item xs={6} id="client-edit-header">
              <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: { xs: 'nowrap', sm: 'wrap', md: 'nowrap' },
                    justifyContent: { xs: 'flex-start', sm: 'space-between' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    width: '100%',
                    padding: { xs: '10px', sm: '15px' },
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    marginBottom: '15px',
                    gap: { xs: '10px', sm: '5px' }
                  }}
                >
                  <Box sx={{ 
                    width: '100%',
                    order: { xs: 1, sm: 1 },
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                        fontWeight: 'bold',
                        color: colors.black
                      }}
                    >
                      {displayName}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexWrap: 'wrap',
                    gap: '8px',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    alignItems: 'center',
                    width: { xs: '100%', md: '75%' },
                    order: { xs: 2, sm: 2 },
                  }}>
                     <Button
                      endIcon={client.income_year_forms ? <RemoveIcon /> : <AddIcon />}
                      variant='link'
                      disabled={savingView}
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                        color: colors.white,
                        backgroundColor: colors.primaryColor,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        '&:hover': {
                          color: colors.white,
                          backgroundColor: colors.primaryAccent,
                        }
                      }}
                      onClick={async () => {
                        setSavingView(true);
                        const updated = await toggleForm(auth, client, 'income_year_forms', !client.income_year_forms);
                        const updatedClient = await generateIncomeByYear(updated);
                        setClient(updatedClient);
                        setSavingView(false);
                      }}
                    >
                      {client.income_year_forms ? 'Hide' : 'Show'} Forms
                    </Button>
                  </Box>
                </Box>
              </Grid>

              {client.income_year_forms && <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    backgroundColor: '#fff', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    p: 2,
                    height: '100%'
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Desired Annual Target Income (ATI):</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>${formatCurrency(client.desired_ati)}</Typography>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Tax Filing Status:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>
                        <CustomSelect
                          id="filing-status-label"
                          label="Filing Status"
                          value={client.filing_status || ''}
                          name="filing_status"
                          onChange={handleTextChange}
                        >
                          <MenuItem value="SINGLE">Single</MenuItem>
                          <MenuItem value="MFJ">MFJ</MenuItem>
                          <MenuItem value="HOH">HoH</MenuItem>
                        </CustomSelect>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Leg 1 Tax Free:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>
                        <Select
                          id="leg1_tax_free-label"
                          label="Leg 1 Tax Free"
                          value={!!client.leg1_tax_free}
                          name="leg1_tax_free"
                          onChange={handleTextChange}
                          style={{ width: '100px' }}
                        >
                          <MenuItem value={true}>Y</MenuItem>
                          <MenuItem value={false}>N</MenuItem>
                        </Select>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Leg 2 Tax Free:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>
                        <Select
                          id="leg2_tax_free-label"
                          label="Leg 2 Tax Free"
                          value={!!client.leg2_tax_free}
                          name="leg2_tax_free"
                          onChange={handleTextChange}
                          style={{ width: '100px' }}
                        >
                          <MenuItem value={true}>Y</MenuItem>
                          <MenuItem value={false}>N</MenuItem>
                        </Select>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Leg 3 Tax Free:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1 }}>
                        <Select
                          id="leg3_tax_free-label"
                          label="Leg 3 Tax Free"
                          value={!!client.leg3_tax_free}
                          name="leg3_tax_free"
                          onChange={handleTextChange}
                          style={{ width: '100px' }}
                        >
                          <MenuItem value={true}>Y</MenuItem>
                          <MenuItem value={false}>N</MenuItem>
                        </Select>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    backgroundColor: '#fff', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    p: 2,
                    height: '100%'
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Assumed Inflation:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MuiTextField
                          type="number"
                          size="small"
                          fullWidth
                          value={client.inflation_rate || ''}
                          onChange={(e) => {
                            let val = e.target.value;
                            setClient({ ...client, inflation_rate: val });
                          }}
                          inputProps={{
                            style: { color: colors.black }
                          }}
                          sx={{ mb: 1 }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Effective Tax Rate:</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <MuiTextField
                          type="number"
                          size="small"
                          fullWidth
                          value={client.effective_tax_rate || ''}
                          onChange={(e) => {
                            let val = e.target.value;
                            setClient({ ...client, effective_tax_rate: val });
                          }}
                          inputProps={{
                            style: { color: colors.black }
                          }}
                          sx={{ mb: 1 }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: colors.black, fontWeight: 'bold', mb: 1 }}>Plan Income After Tax?</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                          <MuiSelect
                            value={planIncomeAfterTax}
                            onChange={(e) => {
                              setPlanIncomeAfterTax(e.target.value);
                            }}
                            sx={{ '& .MuiSelect-select': { color: colors.black } }}
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </MuiSelect>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>}
            </Grid>
            <Box sx={{ 
              overflowX: 'auto', 
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '8px',
              mb: 3,
              bgcolor: 'white'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                margin: '0 auto',
                border: '1px solid #f0f0f0'
              }}>
                <thead>
                  <tr>
                    <th colSpan={3} style={{ 
                      padding: '8px', 
                      backgroundColor: '#f5f8ff', 
                      borderBottom: '1px solid #ddd',
                      color: colors.black
                    }}></th>
                    <th colSpan={6} style={{ 
                      padding: '10px', 
                      backgroundColor: '#15316a',
                      color: 'white',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      borderRadius: '4px 4px 0 0'
                    }}>Base Income</th>
                    {viewAdditionalInformation && (
                      <th colSpan={8} style={{ 
                        padding: '8px', 
                        backgroundColor: '#f5f8ff', 
                        borderBottom: '1px solid #ddd',
                        color: colors.black
                      }}>
                        <Button
                          endIcon={<RemoveIcon/>}
                          variant='link'
                          sx={{
                          color: colors.black,
                          textDecoration: 'underline',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: '#15316a'
                          }
                        }} onClick={() => {
                          setViewAdditionalInformation(false);
                        }}>Collapse</Button>
                      </th>
                    )}
                    {!viewAdditionalInformation && (
                      <th colSpan={3} style={{ 
                        padding: '8px', 
                        backgroundColor: '#f5f8ff', 
                        borderBottom: '1px solid #ddd',
                        color: colors.black
                      }}>
                        <Button
                          endIcon={<AddIcon/>}
                          variant='link'
                          sx={{
                          color: colors.black,
                          textDecoration: 'underline',
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: '#15316a'
                          }
                        }} onClick={() => {
                          setViewAdditionalInformation(true);
                        }}>Expand</Button>
                      </th>
                    )}
                    <th colSpan={5} style={{ 
                      padding: '10px', 
                      backgroundColor: '#15316a', 
                      color: 'white',
                      fontWeight: 'bold',
                      textAlign: 'center',
                      borderRadius: '4px 4px 0 0'
                    }}>Retirement Plan Income</th>
                    <th style={{ 
                      padding: '8px', 
                      backgroundColor: '#f5f8ff', 
                      borderBottom: '1px solid #ddd',
                      color: colors.black
                    }}></th>
                  </tr>
                  <tr>
                    {[
                      'Year',
                      `${client.firstname}'s Age`,
                      client.spouse_firstname ? `${client.spouse_firstname}'s Age` : 'Age2',
                      `${client.firstname}'s W2`,
                      client.spouse_firstname ? `${client.spouse_firstname}'s W2` : 'W2',
                      'Pension',
                      'Rental',
                      `${client.firstname}'s SSI`,
                      client.spouse_firstname ? `${client.spouse_firstname}'s SSI` : 'SSI',
                      ...(viewAdditionalInformation ? [
                        'Provisional Income',
                        'Eff. % SSI Taxable',
                        'Taxable SSI',
                        'Taxable Income',
                        'Estimated Tax',
                      ] : []),
                      'Base Income',
                      'Desired Income',
                      'Gap',
                      'Leg 1',
                      'Leg 2',
                      'Leg 3',
                      'Plan Income Taxes',
                      'Total Planned Income',
                      'Plan Gap'
                    ].map((header, index) => (
                      <th key={index} style={{
                        padding: '8px',
                        backgroundColor: '#f5f8ff',
                        borderBottom: '1px solid #ddd',
                        color: colors.black,
                        fontWeight: 'bold',
                        textAlign: 'center',
                        // whiteSpace: 'nowrap',
                        ...(index === 0 ? {
                          position: 'sticky',
                          left: -1,
                          zIndex: 2,
                          boxShadow: '2px 0 5px -2px rgba(0,0,0,0.2)'
                        } : {}),
                        ...(index === 7 ? {
                          borderTop: '2px solid black',
                          borderBottom: '2px solid black',
                          borderLeft: '2px solid black', 
                          backgroundColor: colors.primaryLighter
                        } : {}),
                        ...(index === 8 ? {
                          borderTop: '2px solid black', 
                          borderBottom: '2px solid black',
                          borderRight: '2px solid black',
                          backgroundColor: colors.primaryLighter
                        } : {}),
                      }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} style={{
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#e9ebff'
                    }}>
                      {row}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            <Box sx={{ flexGrow: 1, marginTop: '25px' }}>
              <Line options={options} data={chartData} />
            </Box>
          </div>
        ) : <CircularProgress />}
      </Box>
    </IncomePlanWrapper>
  );
};


export default ClientIncomeYear;
